import { computed, defineComponent, PropType } from "vue";
import { mapActions, mapState } from "pinia";
import moment from "moment";
import { Container } from "aurelia-dependency-injection";
import { CalendarOptions, EventContentArg } from "@fullcalendar/core";
import { useApp } from "../../../../App/useApp";

import { IRescheduleValidations } from "../../../../../resources/dialogs/reschedule-validations/reschedule-validations";
import { TaskService, type ITaskByUserResponse } from "../../../../../services/task.service";
import { ToastrService } from "../../../../../services/toastr.service";
import { BranchesService } from "../../../../../services/branches-service";
import { NavigationService } from "../../../../../services/navigation.service";
import { LookupService } from "../../../../../services/lookup-service";
import { IGetTaskResponseByUser } from "../../../../../services/user-service";
import { IGetAgency } from "../../../../../services/agency.service";
import { getHospiceRedirectionUrl } from "../../../../../common/get-hospice-redirection-url";

import {
    ApplicationEnum,
    ApplicationNameEnum,
    EmployeeStatusEnum,
    ParentPermissionEnum,
    PermissionActionEnum,
    VisitStatusEnum,
    EvvStatusHomeCareEnum,
    EvvStatusHomeHealthEnum
} from "../../../../../enums/enums";

import { CalendarEmployeeModel } from "../../../../../pages/central-calendar/calendar-employee-model";
import { BulkReassign } from "../../../../../resources-vue/vue-models/bulk-reassign";
import { RescheduleTask } from "../../../../../models/task.model";

import {
    IVisitDetails,
    IRescheduleVisitHosPal,
    IHandleTaskReschuleIssue,
    IReassignCalendarVisitHC,
    IGetTaskComment
} from "../../../../../resources-vue/vue-interfaces/i-visit-details";
import {
    IDisciplineTaskHH,
    IGetDisciplineTaskHHParams
} from "../../../../../resources-vue/vue-interfaces/i-discipline-task-hh";
import { IReassignVisitDetails } from "../../../../../resources-vue/vue-interfaces/i-reassign-visit";
import { IDeleteTaskPayload, IRestoreMissedVisitRequest } from "../../../../../resources-vue/vue-interfaces/i-task";
import { IBulkDeletePayload } from "../../../../../resources-vue/vue-interfaces/i-bulk-reassign-visit";

import { adjustDate } from "../../../../../common/adjust-date";
import { PermissionManager } from "../../../../../common/utilities/permission-manager";
import { useLoadingIndicator } from "../../../../Common/LoadingIndicator/store/useLoadingIndicator";
import { formatName } from "../../../../../common/format-name";
import config from "../../../../../common/config";
import { IPromptOptions } from "../../../../../resources-vue/vue-dialogs/prompt/prompt";
import { FormatPhoneNumber } from "../../../../../resources/value-converters/format-phone";
import { GetProductClass } from "../../../../../resources/value-converters/get-product-class";
import { ScheduleLegendState } from "../../../../../pages/central-calendar/central-calendar";
import {
    formatScheduledTime,
    formatTimeInOut,
    getTaskReturnComments
} from "../../../../../common/vue-helpers/modifiers/value-modifier";
import { PatientService } from "../../../../../services/patient.service";
import { PayType } from "../../../../../enums/PayTypeEnum";
import { CommentColorEnum } from "../../../../../enums/CommentColorEnum";
import { StickyNoteTypeEnum } from "../../../../../enums/StickyNoteTypeEnum";
import { AnalyticsService, AnalyticsCategory } from "../../../../../services/analytics.service";
import { isMobileScreenSize } from "../../../../../utilities/isMobileScreenSize";
import { helperTestingAgency } from "../../../../../common/vue-helpers/testing-agency-check";
import { truncateText } from "../../../../../utilities/truncateString";

import ScheduleVisitEmployee from "../../../../../resources-vue/vue-dialogs/ScheduleVisit/ScheduleVisitEmployee.vue";
import FullCalendar from "../../../../../resources-vue/vue-elements/FullCalendar/FullCalendar.vue";
import RescheduleValidation from "../../../../../resources-vue/vue-dialogs/RescheduleValidations/RescheduleValidation.vue";
import ReassignVisit from "../../../../../resources-vue/vue-dialogs/ReassignVisit/ReassignVisit.vue";
import VisitDetails from "../../../../../resources-vue/vue-dialogs/VisitDetails/VisitDetails.vue";
import PatientProfile from "../../../../../resources-vue/vue-dialogs/PatientProfile/PatientProfile.vue";
import Prompt from "../../../../../resources-vue/vue-dialogs/prompt/prompt.vue";
import EvvDetails from "../../../../../resources-vue/vue-dialogs/EvvDetails/EvvDetails.vue";

export default defineComponent({
    props: {
        startDate: { type: Date },
        endDate: { type: Date },
        usersList: { type: Array as PropType<CalendarEmployeeModel[]>, default: [] },
        tasksList: { type: Array, default: [] },
        tasksMap: { default: null },
        onCalendarRefresh: { type: Function },
        toggleFindNearByClientsView: { type: Function },
        employeeScheduleSummary: { type: Array, default: [] },
        isPremiumByAgency: {
            type: Object,
            default: null
        }
    },

    components: {
        FullCalendar
    },

    data() {
        return {
            _analyticsService: null as AnalyticsService,
            _patientsService: null,
            isLoading: false as boolean,
            _navigationService: null,
            _permissionManager: null,
            _taskService: null as TaskService,
            application: "",
            patientDetails: null,
            _toastrService: null,
            _branchService: null,
            _lookupService: null,
            dialogRef: null,
            editable: true,
            disciplineTasks: [] as IDisciplineTaskHH[],
            // NOTE: eventLimit be one more than the required events, as to account for dummy event
            eventLimit: 4 as number,
            userNamesMap: null,
            showEditBtn: false as boolean,
            hasTestAgency: false as boolean,
            isReassignAllowed: false as boolean,
            isEditVisitDetailsAllowed: false as boolean,
            trackingReasonId: "" as string,
            trackingTypeId: "" as string,
            employeeStatuses: Object.create(EmployeeStatusEnum),
            PayType: PayType,
            organizationId: null as string,
            agencyList: [] as IGetAgency[],
            getTaskReturnComments,
            isMobileDevice: false as boolean,
            canViewTaskNotesHos: false as boolean,
            canViewTaskNotesPal: false as boolean
        };
    },

    provide() {
        return {
            dialogRef: computed(() => this.dialogRef)
        };
    },

    emits: ["calendarRefresh"],

    async created() {
        this._permissionManager = Container.instance.get(PermissionManager);
        this._taskService = Container.instance.get(TaskService);
        this._toastrService = Container.instance.get(ToastrService);
        this._branchService = Container.instance.get(BranchesService);
        this._navigationService = Container.instance.get(NavigationService);
        this._lookupService = Container.instance.get(LookupService);
        this._patientsService = Container.instance.get(PatientService);
        this._analyticsService = Container.instance.get(AnalyticsService);
        this.hasTestAgency = await helperTestingAgency.checkHasTestingAgency();
    },

    async mounted() {
        this.isMobileDevice = isMobileScreenSize();
        this.canViewTaskNotesHos = this._permissionManager.checkPermissionByProduct(
            ParentPermissionEnum.taskNotes,
            PermissionActionEnum.canView,
            ApplicationEnum.AxxessHospiceFE
        );
        this.canViewTaskNotesPal = this._permissionManager.checkPermissionByProduct(
            ParentPermissionEnum.taskNotes,
            PermissionActionEnum.canView,
            ApplicationEnum.AxxessPalliative
        );
        this.isReassignAllowed = this._permissionManager.checkPermission(
            ParentPermissionEnum.clinician,
            PermissionActionEnum.canReassign
        );
        this.isEditVisitDetailsAllowed = this._permissionManager.checkPermission(
            ParentPermissionEnum.scheduler,
            PermissionActionEnum.canEdit
        );
        this.showEditBtn = this.isReassignAllowed || this.isEditVisitDetailsAllowed;
        this.organizationId = window.localStorage.getItem(config.organizationId);
        this.agencyList = this.organizations
            .find((organization: { id: string }) => organization.id === this.organizationId)
            .agencies?.map((agency) => ({
                ...agency,
                // this is done because Application property of AxxessHospiceFE tasks were updated to AxxessHospice in taskActions()
                application:
                    agency.application === ApplicationEnum.AxxessHospiceFE
                        ? ApplicationEnum.AxxessHospice
                        : agency.application
            }));
    },

    computed: {
        ...mapState(useApp, ["organizations"]),
        userDetails() {
            let userEntries: any[] = [];
            this.userNamesMap = new Map();
            this.usersList?.forEach((user) => {
                user.Agencies.forEach((op) => {
                    let userData = {
                        ...user,
                        ...op,
                        groupId: user.Id,
                        id: `${op.UserId}${op.Application}`
                    };
                    userEntries.push(userData);
                });
                this.userNamesMap.set(
                    user.Id,
                    formatName(user.FirstName, user.LastName, "LN, FN", "", user.MiddleInitial)
                );
            });
            return userEntries ?? [];
        },

        taskActions() {
            let tasksMap = new Map();
            this.tasksList.forEach((task: any) => {
                let startDate = new Date(Date.parse(task.StartDate));
                if (task.Application === 256) {
                    task.Application = ApplicationEnum.AxxessHospice;
                }
                let key = `${task.UserId}${startDate.toLocaleDateString()}`;
                if (!tasksMap.has(key)) {
                    tasksMap.set(key, [task]);
                } else {
                    let currentTasks = tasksMap.get(key);
                    tasksMap.set(key, [...currentTasks, task]);
                }
            });
            let tasksList: ITaskByUserResponse[] = [];
            tasksMap.forEach((value: any, key: any) => {
                tasksList.push(...value);
                // let list = value.slice(0, this.eventLimit);
                // tasksList = tasksList.concat([...list]);
                // if (value.length > this.eventLimit) {
                //     let extraTask = {
                //         Id: value[0].Id,
                //         UserId: value[0].UserId,
                //         StartDate: moment(value[0].StartDate).endOf("day").format(),
                //         EndDate: value[0].StartDate,
                //         Application: value[0].Application,
                //         isExtraEvent: true
                //     } as ITaskByUserResponse;
                //     tasksList.push(extraTask);
                // }
            });
            return (
                tasksList?.map((task) => ({
                    ...task,
                    id: task.Id,
                    start: task.StartDate,
                    end: moment(task.EndDate).endOf("day").format(),
                    resourceId: `${task.UserId}${task.Application}`
                })) ?? []
            );
        },

        calendarOptions(): CalendarOptions {
            return {
                resources: this.userDetails,
                resourceOrder: "LastName, FirstName MiddleInitial",
                events: this.taskActions,
                resourceGroupField: "groupId",
                resourceAreaHeaderContent: "Employee Name",
                resourceAreaWidth: this.isMobileDevice ? 150 : 310,
                droppable: true,
                contentHeight: "auto",
                eventDragStart: (event: any) => {
                    $('[role="tooltip"]')?.remove();
                    document.addEventListener("mousemove", this.handleEventDrag);
                },
                eventDragStop: (event: any) => {
                    document.removeEventListener("mousemove", this.handleEventDrag);
                },
                eventDrop: async (event: any) => {
                    $('[role="tooltip"]')?.remove();
                    document.removeEventListener("mousemove", this.handleEventDrag);
                    const rowChanged = !(
                        JSON.stringify(event.oldEvent._def.resourceIds) == JSON.stringify(event.event._def.resourceIds)
                    );
                    if (!event?.event?.extendedProps || !event?.event?.startStr) {
                        this._toastrService.error({
                            title: `Error - Rescheduling task`,
                            message: `There was an error rescheduling the visit. Please Try again later.`
                        });
                        this.$emit("calendarRefresh");
                        return;
                    }
                    const visitDetails = event.event.extendedProps;
                    if (![VisitStatusEnum.NotStarted, VisitStatusEnum.NotYetDue].includes(visitDetails.Status)) {
                        this._toastrService.error({
                            title: `Error`,
                            message: `Visit must be in Not yet Started and Not Yet Due status.`
                        });
                        event.revert();
                        return;
                    }
                    if (
                        ![EvvStatusHomeCareEnum.NotStarted].includes(
                            visitDetails.EvvIconStatus as EvvStatusHomeCareEnum
                        ) ||
                        ![EvvStatusHomeHealthEnum.NotStarted].includes(
                            visitDetails.VerificationVisitStatus as EvvStatusHomeHealthEnum
                        )
                    ) {
                        this._toastrService.error({
                            title: `Error`,
                            message: `Visit EVV status must be in Not Started status.`
                        });
                        event.revert();
                        return;
                    }

                    const lastDay = moment(visitDetails.StartDate).endOf("week").format("YYYYMMDD");
                    let newRes: any;
                    let oldRes: any;

                    if (rowChanged) {
                        newRes = event.newResource._resource.extendedProps;
                        oldRes = event.oldResource._resource.extendedProps;

                        if (
                            newRes.Application !== oldRes.Application ||
                            newRes.AgencyId !== oldRes.AgencyId ||
                            newRes.Agencies[0].LocationId !== oldRes.Agencies[0].LocationId
                        ) {
                            this._toastrService.error({
                                title: `Error - Rescheduling task`,
                                message: `Both Employees must belong to the same agency and branch.`
                            });
                            event.revert();
                            return;
                        }

                        if (oldRes.Application === ApplicationEnum.HomeCare) {
                            const trackingReason = await this._taskService.getTrackingReasons({
                                AgencyId: oldRes.AgencyId,
                                Application: oldRes.Application,
                                CategoryId: 1
                            });
                            const trackingTypes = await this._taskService.getTrackingTypes({
                                AgencyId: oldRes.AgencyId,
                                Application: oldRes.Application,
                                CategoryId: 1
                            });
                            if (trackingReason.length && trackingTypes.length) {
                                this.trackingReasonId = trackingReason.find(
                                    (reason: any) =>
                                        reason.TypeName?.toLowerCase().includes("office") &&
                                        reason.CategoryName.toLowerCase().includes("reassign")
                                )?.Id;
                                this.trackingTypeId = trackingTypes.find((type: any) =>
                                    type.Name.toLowerCase().includes("office")
                                )?.Id;
                                if (!this.trackingReasonId && !this.trackingTypeId) {
                                    this._toastrService.error({
                                        title: `Error - Rescheduling task`,
                                        message: `Agency doesn't have required reason for rescheduling task`
                                    });
                                    event.revert();
                                    return;
                                }
                            } else {
                                this._toastrService.error({
                                    title: `Error - Rescheduling task`,
                                    message: `Failed to fetch reasons for rescheduling task. Please try again`
                                });
                                event.revert();
                                return;
                            }
                        }
                        if (
                            newRes.Application === ApplicationEnum.AxxessHospice ||
                            newRes.Application === ApplicationEnum.AxxessPalliative
                        ) {
                            const queryParams = {
                                customTaskId: visitDetails.CustomTaskId,
                                application:
                                    visitDetails.Application === ApplicationEnum.AxxessHospice
                                        ? ApplicationEnum.AxxessHospiceFE
                                        : visitDetails.Application,
                                accountId: visitDetails.AgencyId,
                                patientId: visitDetails.PatientId
                            };
                            let employees = await this._taskService.employeeList(queryParams);
                            const isValidEmp = employees.some((emp: any) => emp.id === newRes.UserId);
                            if (!isValidEmp) {
                                this._toastrService.error({
                                    title: `Error - Rescheduling task`,
                                    message: `Selected Employee do not have the required credentials for this visit.`
                                });
                                event.revert();
                                return;
                            }
                        }
                    }

                    if (
                        visitDetails.StartDate != visitDetails.EndDate &&
                        moment(event.event.start).format("YYYYMMDD") == lastDay
                    ) {
                        this._toastrService.error({
                            title: `Error - Rescheduling task`,
                            message: `Midnight split visit will not get reschedule to the last day of the week`
                        });
                        this.$emit("calendarRefresh");
                        return;
                    }

                    if (
                        [
                            ApplicationEnum.AxxessHospiceFE,
                            ApplicationEnum.AxxessHospice,
                            ApplicationEnum.AxxessPalliative
                        ].includes(visitDetails.Application)
                    ) {
                        let startTime = visitDetails.StartTime
                            ? moment(visitDetails.StartTime).format("HH:mm:ss")
                            : "00:00:00";
                        let endTime = visitDetails.EndTime
                            ? moment(visitDetails.EndTime).format("HH:mm:ss")
                            : "00:00:00";
                        this.rescheduleVisitHosPal(
                            visitDetails,
                            `${moment(event.event.start).format("MM/DD/YYYY")} ${startTime}`,
                            `${moment(event.event.end).format("MM/DD/YYYY")} ${endTime}`,
                            rowChanged ? newRes.UserId : visitDetails.UserId
                        );
                    } else if (visitDetails.Application == ApplicationEnum.HomeCare) {
                        let timeIn = visitDetails.StartTime || visitDetails.timeIn;
                        timeIn = timeIn ? moment(timeIn).format("HH:mm:ss") : "";
                        let timeOut = visitDetails.EndTime || visitDetails.timeOut;
                        timeOut = timeOut ? moment(timeOut).format("HH:mm:ss") : "";
                        const newStart = moment(event.event.start).format("YYYY-MM-DD");
                        const newEnd = moment(event.event.end).format("YYYY-MM-DD");
                        this.rescheduleVisitHomeCare(
                            visitDetails,
                            `${newStart}${timeIn ? "T" + timeIn : ""}`,
                            `${newEnd}${timeOut ? "T" + timeOut : ""}`,
                            rowChanged ? newRes.UserId : visitDetails.UserId,
                            rowChanged
                        );
                    } else {
                        let timeIn = visitDetails.StartTime || visitDetails.timeIn;
                        timeIn = timeIn && timeIn !== "00:00:00" ? moment(timeIn).format("HH:mm:ss") : null;
                        let timeOut = visitDetails.EndTime || visitDetails.timeOut;
                        timeOut = timeOut && timeOut !== "00:00:00" ? moment(timeOut).format("HH:mm:ss") : null;
                        const newStart = moment(event.event.start).format("YYYY-MM-DD");
                        const newEnd = moment(event.event.end).format("YYYY-MM-DD");
                        const serviceLocation = visitDetails.ServiceLocation;
                        const customId = visitDetails.CustomId;
                        const taskName = visitDetails.TaskName;
                        const timeInFormatted = timeIn && timeIn !== "00:00:00" ? `${newStart}T${timeIn}` : null;
                        const timeOutFormatted = timeOut && timeOut !== "00:00:00" ? `${newEnd}T${timeOut}` : null;
                        this.rescheduleVisit(
                            visitDetails,
                            moment(event.event.start).format("MM/DD/YYYY"),
                            timeInFormatted,
                            timeOutFormatted,
                            customId,
                            taskName,
                            serviceLocation,
                            rowChanged ? newRes.UserId : visitDetails.UserId
                        );
                    }
                    this.filterDuplicateEvent(event);
                },

                eventContent: (arg: EventContentArg) => {
                    const eventData = arg?.event?.extendedProps;
                    let extraEvents = 0;
                    if (eventData?.isExtraEvent) {
                        let startDate = new Date(Date.parse(eventData?.StartDate));
                        let key = `${eventData.UserId}${startDate.toLocaleDateString()}`;
                        let currentTaskList = this.tasksMap.get(key);
                        extraEvents = currentTaskList.length - this.eventLimit;
                    }
                    return {
                        html: this.generateEvent(arg, extraEvents)
                    };
                },

                eventDidMount: (arg: any) => {
                    const self = this;
                    const scheduledEvent = arg?.event?.extendedProps;
                    const reassignBtn = `btn-reassign-${scheduledEvent.Id}`;
                    const visitDetailsBtn = `btn-visitdetails-${scheduledEvent.Id}`;
                    const profileBtn = `btn-profile-${scheduledEvent.Id}`;
                    const patientChartBtn = `btn-patientchart-${scheduledEvent.Id}`;
                    const deleteVisitBtn = `btn-deletevisit-${scheduledEvent.Id}`;
                    const restoreVisitBtn = `btn-restorevisit-${scheduledEvent.Id}`;
                    const evvHouse = `evv-house-${scheduledEvent.Id}`;
                    const yellowNote = `yellow-note-${scheduledEvent.Id}`;
                    const orangeNote = `orange-note-${scheduledEvent.Id}`;
                    const redNote = `red-note-${scheduledEvent.Id}`;
                    const blueNote = `blue-note-${scheduledEvent.Id}`;
                    const blueTooltip = `blue-tooltip-${scheduledEvent.Id}`;
                    const orangeTooltip = `orange-tooltip-${scheduledEvent.Id}`;
                    const redTooltip = `red-tooltip-${scheduledEvent.Id}`;
                    const yellowTooltip = `yellow-tooltip-${scheduledEvent.Id}`;

                    const hidePopover = () => $(arg?.el).popover("hide");
                    const showPopover = () => {
                        self.attachTooltip(arg);
                        $(arg?.el).popover("show");
                    };
                    const registerNoteHover = (noteId: string, color: CommentColorEnum) => {
                        const noteE1 = document.getElementById(noteId);
                        if (!noteE1 || noteE1.dataset.hoverRegistered) {
                            return;
                        }
                        noteE1.addEventListener("mouseenter", (event: MouseEvent) => {
                            event.stopPropagation();
                            event.preventDefault();
                            hidePopover();
                            self.attachVisitTooltip(arg, color);
                        });
                        noteE1.addEventListener("mouseleave", () => {
                            $(`.visit-${color}-note[role="tooltip"]`)?.remove();
                            showPopover();
                        });
                        noteE1.dataset.hoverRegistered = "true";
                    };
                    $('[role="tooltip"]')?.remove();
                    self.attachTooltip(arg);

                    if (!!document.getElementById(reassignBtn)) {
                        document.getElementById(reassignBtn).addEventListener("click", () => {
                            // adding this click event listener manually because calling onclick event directly causes issue since DOM loads first before the JS.
                            self.scheduledVisit(arg);
                        });
                    }

                    if (!!document.getElementById(evvHouse)) {
                        document.getElementById(evvHouse).addEventListener("click", () => {
                            self.openEvvHouse(arg);
                        });
                    }

                    if (!!document.getElementById(orangeNote)) {
                        document.getElementById(orangeNote).addEventListener("click", () => {
                            self.openVisitNote(arg, CommentColorEnum.orange);
                        });
                    }
                    if (!!document.getElementById(redNote)) {
                        document.getElementById(redNote).addEventListener("click", () => {
                            self.openVisitNote(arg, CommentColorEnum.red);
                        });
                    }

                    if (!!document.getElementById(yellowNote)) {
                        document.getElementById(yellowNote).addEventListener("click", () => {
                            self.openVisitNote(arg, CommentColorEnum.yellow);
                        });
                    }

                    if (!!document.getElementById(blueNote)) {
                        document.getElementById(blueNote).addEventListener("click", () => {
                            self.openVisitNote(arg, CommentColorEnum.blue);
                        });
                    }

                    registerNoteHover(yellowTooltip, CommentColorEnum.yellow);
                    registerNoteHover(orangeTooltip, CommentColorEnum.orange);
                    registerNoteHover(redTooltip, CommentColorEnum.red);
                    registerNoteHover(blueTooltip, CommentColorEnum.blue);

                    if (!!document.getElementById(visitDetailsBtn)) {
                        document.getElementById(visitDetailsBtn).addEventListener("click", () => {
                            self.showVisitDetails(arg);
                        });
                    }
                    if (!!document.getElementById(profileBtn)) {
                        document.getElementById(profileBtn).addEventListener("click", () => {
                            self.openProfile(arg);
                        });
                    }
                    if (!!document.getElementById(patientChartBtn)) {
                        document.getElementById(patientChartBtn).addEventListener("click", () => {
                            self.handlePatientNameClick(arg);
                        });
                    }

                    if (!!document.getElementById(deleteVisitBtn)) {
                        document.getElementById(deleteVisitBtn).addEventListener("click", () => {
                            self.deleteTask(arg);
                        });
                    }

                    if (!!document.getElementById(restoreVisitBtn)) {
                        document.getElementById(restoreVisitBtn).addEventListener("click", () => {
                            self.initiateRestoreTask(arg);
                        });
                    }

                    // click event listener for edit icon
                    const editButtonId = `edit-btn-${scheduledEvent.Id}`;
                    if (!!document.getElementById(editButtonId)) {
                        document.getElementById(editButtonId).addEventListener("click", () => {
                            $('[role="tooltip"]')?.remove();

                            self.tasksList.forEach((task: any) => {
                                const selectorElement = $(`#scheduled-event-${task.Id}`).closest("a")[0];
                                if (selectorElement) {
                                    if (task.Id != scheduledEvent.Id) {
                                        selectorElement.style.zIndex = "0";
                                    } else {
                                        selectorElement.style.zIndex = "5";
                                    }
                                }
                            });
                        });
                    }

                    // Permission Logic
                    const editBtnId = `edit-icon-${scheduledEvent.Id}`;
                    let showEditButtonForVisit: boolean = false;
                    let showReassignButtonForVisit: boolean = false;
                    showReassignButtonForVisit =
                        self.isReassignAllowed &&
                        (scheduledEvent.Status == VisitStatusEnum.NotStarted ||
                            scheduledEvent.Status == VisitStatusEnum.NotYetDue) &&
                        scheduledEvent.EvvIconStatus !== EvvStatusHomeCareEnum.Finished &&
                        scheduledEvent.EvvIconStatus !== EvvStatusHomeCareEnum.FinishedAdjusted &&
                        scheduledEvent.EvvIconStatus !== EvvStatusHomeCareEnum.InProgress &&
                        scheduledEvent.EvvIconStatus !== EvvStatusHomeCareEnum.InProgressAdjusted &&
                        scheduledEvent.VerificationVisitStatus !== EvvStatusHomeHealthEnum.InProgress &&
                        scheduledEvent.VerificationVisitStatus !== EvvStatusHomeHealthEnum.PatientVerified &&
                        scheduledEvent.VerificationVisitStatus !== EvvStatusHomeHealthEnum.ClinicianVerified;
                    let showVisitDetailsButtonForVisit: boolean = false;
                    showVisitDetailsButtonForVisit =
                        self.isEditVisitDetailsAllowed &&
                        (scheduledEvent.Application == ApplicationEnum.AgencyCore ||
                            scheduledEvent.Application == ApplicationEnum.HomeCare ||
                            scheduledEvent.Application == ApplicationEnum.AxxessPalliative ||
                            scheduledEvent.Application == ApplicationEnum.AxxessHospice);
                    showEditButtonForVisit =
                        self.showEditBtn && (showReassignButtonForVisit || showVisitDetailsButtonForVisit);
                    if (!!document.getElementById(editBtnId)) {
                        if (!showEditButtonForVisit) {
                            document.getElementById(editBtnId).style.display = "none";
                            return;
                        }
                        if (!!document.getElementById(reassignBtn) && !showReassignButtonForVisit) {
                            document.getElementById(reassignBtn).style.display = "none";
                        }
                        if (!!document.getElementById(visitDetailsBtn) && !showVisitDetailsButtonForVisit) {
                            document.getElementById(visitDetailsBtn).style.display = "none";
                        }
                    }
                },

                // NOTE: Uncomment when task navigation is needed
                // eventClick: (event: any) => {
                //     if (!$(event.el).find(".more-btn").length) {
                //         this.handleEventClick(event);
                //     }
                // },
                resourceLabelContent: (arg: any) => {
                    $('[role="tooltip"]')?.remove();
                    return {
                        html: this.generateResource(arg)
                    };
                },
                resourceLabelDidMount: (arg: any) => {
                    this.registerClick(arg);
                },
                resourceGroupLabelContent: (arg: any) => {
                    return {
                        html: this.generateGroupLabel(arg)
                    };
                },

                visibleRange: {
                    start: this.startDate,
                    end: adjustDate(this.endDate, 1)
                }
            };
        }
    },

    methods: {
        ...mapActions(useLoadingIndicator, { showLoading: "SET_LOADING" }),
        generateGroupLabel(arg: any) {
            let groupId = arg?.groupValue;
            let employeeName = this.userNamesMap?.get(groupId);
            let labelContent = "";
            labelContent = `<div class="d-flex"><span class="text-dark fw-bold text-truncate "> ${employeeName} </span></div>`;
            return labelContent;
        },

        async scheduledVisit(arg: any) {
            this._analyticsService.logEvent({
                category: AnalyticsCategory.CentralVisitActions,
                action: "Employee calendar reassign visit"
            });
            const taskDetails = arg?.event?.extendedProps;
            const userDetails = this.usersList.find((user) =>
                user.Agencies.some((Agency) => Agency.UserId == taskDetails.UserId)
            );
            const additionalInfo = {
                AgencyName: userDetails.Agencies[0].AgencyName,
                FirstName: taskDetails.PatientFirstName,
                LastName: taskDetails.PatientLastName,
                UserFirstName: userDetails.FirstName,
                UserLastName: userDetails.LastName
            };
            let visitDetails: IReassignVisitDetails = Object.assign({}, additionalInfo, taskDetails);
            visitDetails.Application = visitDetails.Application == -3 ? 256 : visitDetails.Application;
            this.dialogRef = this.$dialog.open(ReassignVisit, {
                props: {
                    modal: true,
                    showHeader: false,
                    contentStyle: {
                        minWidth: "650px",
                        minHeight: visitDetails.Application === ApplicationEnum.HomeCare ? "350px" : "280px"
                    }
                },
                data: {
                    visitDetails
                },
                onClose: async (options) => {
                    if (options.data === "success") {
                        this.$emit("calendarRefresh");
                    }
                }
            });
        },

        async showVisitDetails(arg: any) {
            const taskDetails = arg?.event?.extendedProps as IVisitDetails;
            const visitDetails: IVisitDetails = Object.assign({}, taskDetails);
            visitDetails.isEmployeeCalendar = true;
            if (visitDetails.Application == ApplicationEnum.AxxessHospice) {
                visitDetails.Application = 256;
            }
            this.dialogRef = this.$dialog.open(VisitDetails, {
                props: {
                    modal: true,
                    showHeader: false
                },
                data: {
                    visitDetails: visitDetails
                },
                onClose: (response) => {
                    if (response.data === "success") {
                        this.$emit("calendarRefresh");
                    }
                }
            });
        },

        async openProfile(arg: any) {
            const taskDetails = arg?.event?.extendedProps;
            try {
                const data = Object.assign({}, taskDetails);
                const payload = {
                    agencyId: data.AgencyId,
                    application:
                        data.Application === ApplicationEnum.AxxessHospice
                            ? ApplicationEnum.AxxessHospiceFE
                            : data.Application,
                    patientId: data.PatientId
                };
                this.application = data.Application;
                const response = await this._patientsService.getPatientsProfile(payload);
                if (response.IsSuccessful) {
                    this.patientDetails = response.data;
                } else {
                    this._toastrService.error({
                        title: `Error`,
                        message: `There was a problem while loading the Patient Information. Please try again. If the problem persists, please report it.`
                    });
                }
            } catch (err) {
                this._toastrService.error({
                    title: `Error`,
                    message: `There was a problem while loading the Patient Information. Please try again. If the problem persists, please report it.`
                });
            }

            const patientInfo = (({ BranchName: AgencyName, AgencyId, Application }) => ({
                AgencyName,
                AgencyId,
                Application
            }))(taskDetails);
            const patientData: BulkReassign = Object.assign({}, this.patientDetails, patientInfo);
            this.dialogRef = this.$dialog.open(PatientProfile, {
                props: {
                    modal: true,
                    showHeader: false,
                    style: "width: 750px"
                },
                data: {
                    patientData
                }
            });
        },

        async handlePatientNameClick(arg: any) {
            let redirectResponse;
            let action = "";
            const taskDetails = arg?.event?.extendedProps;

            if (taskDetails.Application === ApplicationEnum.AxxessHospice) {
                redirectResponse = {
                    redirectUrl: `${getHospiceRedirectionUrl(taskDetails.AgencyId)}/patients/patient-chart/${taskDetails.PatientId}?accountId=${taskDetails.AgencyId}`,
                    isSuccessful: true
                };
                action = "AxxessHospice";
            } else {
                const isClinician = this._permissionManager.checkPermission(
                    ParentPermissionEnum.clinician,
                    PermissionActionEnum.canView
                );
                this.showLoading(true);
                redirectResponse = await this._navigationService.navigateToPatient({
                    agencyId: taskDetails.AgencyId,
                    applicationId: taskDetails.Application,
                    isClinician: isClinician,
                    patientId: taskDetails.PatientId
                });
                action = taskDetails.Application === ApplicationEnum.AgencyCore ? "AxxessHomeHealth" : "AxxessHomeCare";
                this.showLoading(false);
            }

            this._navigationService.redirect(redirectResponse, {
                category: AnalyticsCategory.CentralCalendarNavigation,
                action
            });
        },

        openEvvHouse(arg: any) {
            const taskDetails = arg?.event?.extendedProps as IVisitDetails;
            const visitDetails: IVisitDetails = Object.assign({}, taskDetails);
            visitDetails.isEmployeeCalendar = true;
            this.dialogRef = this.$dialog.open(EvvDetails, {
                props: {
                    modal: true,
                    showHeader: false
                },
                data: {
                    visitDetails: visitDetails
                }
            });
        },

        getStatusText(status: number): string {
            let result = this.employeeStatuses[status].toLowerCase();

            if (result.includes("pending")) {
                return "Pending";
            } else if (result.includes("terminate")) {
                return "Terminated";
            }
            return result;
        },

        async rescheduleVisit(
            event: any,
            date: string,
            StartDate: string,
            EndDate: string,
            customId: number,
            taskName: string,
            serviceLocation: string,
            UserId: string
        ) {
            const visitDetails = event as IVisitDetails;
            const rescheduleVisitDetails = new RescheduleTask();
            rescheduleVisitDetails.fillRequiredDetails(visitDetails);
            rescheduleVisitDetails.EventDate = rescheduleVisitDetails.VisitDate = date || null;
            rescheduleVisitDetails.Status = visitDetails.StatusEnum.toString();
            rescheduleVisitDetails.TimeIn = StartDate;
            rescheduleVisitDetails.TimeOut = EndDate;
            rescheduleVisitDetails.ServiceLocation = serviceLocation;
            rescheduleVisitDetails.UserId = UserId;
            rescheduleVisitDetails.Comments = this.getComment(event);

            await this.getDisciplineTasks(visitDetails);
            const disciplineData = this.disciplineTasks.filter(
                (item: any) => item.customId === customId && item.taskName === taskName
            )[0];
            if (disciplineData) {
                rescheduleVisitDetails.Discipline = disciplineData.discipline;
                rescheduleVisitDetails.DisciplineTask = disciplineData.disciplineTask;
                rescheduleVisitDetails.CustomId = Number(disciplineData.customId);
            } else {
                rescheduleVisitDetails.Discipline = String(visitDetails.Discipline);
                rescheduleVisitDetails.DisciplineTask = visitDetails.DisciplineTask;
                rescheduleVisitDetails.CustomId = customId;
            }

            if (rescheduleVisitDetails.EventDate) {
                try {
                    await this._taskService.rescheduleTask(rescheduleVisitDetails);
                    this._toastrService.success({
                        title: `Success`,
                        message: `The visit has been successfully rescheduled.`
                    });
                } catch (error) {
                    this._toastrService.error({
                        title: `Error - Rescheduling task`,
                        message: `There was an error rescheduling the visit. Please Try again later.`
                    });
                } finally {
                    this.$emit("calendarRefresh");
                }
            } else {
                this._toastrService.error({
                    title: `Error - Rescheduling task`,
                    message: `There was an error rescheduling the visit. Please Try again later.`
                });

                this.$emit("calendarRefresh");
            }
        },

        generateResource(arg: any) {
            let resource = arg?.resource?.extendedProps;
            let userInfo = "";
            let userName = `<span class="fw-bold"> ${formatName(
                resource.FirstName,
                resource.LastName,
                "LN, FN",
                "",
                resource.MiddleInitial
            )} </span>`;
            const phoneNumber = new FormatPhoneNumber().toView(resource.PhoneNumber?.toString());
            const isHomeCareAddAllowed = this._permissionManager.checkPermissionByProduct(
                ParentPermissionEnum.scheduler,
                PermissionActionEnum.canAdd,
                ApplicationEnum.HomeCare
            );
            const isHosAddAllowed = this._permissionManager.checkPermissionByProduct(
                ParentPermissionEnum.scheduler,
                PermissionActionEnum.canAdd,
                ApplicationEnum.AxxessHospiceFE
            );
            const isPalAddAllowed = this._permissionManager.checkPermissionByProduct(
                ParentPermissionEnum.scheduler,
                PermissionActionEnum.canAdd,
                ApplicationEnum.AxxessPalliative
            );
            const isHomeHealthAddAllowed = this._permissionManager.checkPermissionByProduct(
                ParentPermissionEnum.scheduler,
                PermissionActionEnum.canAdd,
                ApplicationEnum.AgencyCore
            );

            const hasSchedulerViewPermissionHomeCare = this._permissionManager.checkPermissionByProduct(
                ParentPermissionEnum.scheduler,
                PermissionActionEnum.canAdd,
                ApplicationEnum.HomeCare
            );
            const hasSchedulerViewPermissionHos = this._permissionManager.checkPermissionByProduct(
                ParentPermissionEnum.scheduler,
                PermissionActionEnum.canAdd,
                ApplicationEnum.AxxessHospiceFE
            );
            const hasSchedulerViewPermissionPal = this._permissionManager.checkPermissionByProduct(
                ParentPermissionEnum.scheduler,
                PermissionActionEnum.canAdd,
                ApplicationEnum.AxxessPalliative
            );
            const hasSchedulerViewPermissionHomeHealth = this._permissionManager.checkPermissionByProduct(
                ParentPermissionEnum.scheduler,
                PermissionActionEnum.canAdd,
                ApplicationEnum.AgencyCore
            );

            if (!!resource) {
                let productClass = new GetProductClass();
                let application = resource.Application;
                if (application === 256) {
                    application = ApplicationEnum.AxxessHospice;
                }
                if (application !== ApplicationEnum.AgencyCore) {
                    userName = `<a class="w-100 font-size-base ${this.isMobileDevice ? "" : "text-truncate"} cursor-pointer user-link" id="${
                        resource.UserId
                    }-${resource.Application}">
                            ${formatName(resource.FirstName, resource.LastName, "LN, FN", "", resource.MiddleInitial)}
                        </a>`;
                }

                userInfo = `<div class="mx-2 d-flex flex-column align-items-start p-2">
                                <span class="fw-normal font-size-xs badge badge-pill pill me-1 ${productClass.toView(
                                    application
                                )}">
                                    ${ApplicationNameEnum[application]}
                                </span>
                                <div class="position-absolute bulk-reassign-wrapper">
                                    <i data-bs-toggle="dropdown" class="fas fa-ellipsis-h cursor-pointer all-actions" id="reassign-ellipsis-${resource.UserId}-${resource.AgencyId}"></i>
                                    <div class="dropdown-menu py-1" aria-labelledby="reassign-ellipsis-${
                                        resource.UserId
                                    }-${resource.AgencyId}">
                                        <a
                                            class="dropdown-item font-size-sm bulk-reassign"
                                            href="javascript:void(0);"
                                            id="task-manager-${resource.UserId}-${resource.AgencyId}"
                                        >
                                            Task Manager
                                        </a>
                                         ${
                                             (application == ApplicationEnum.AxxessHospiceFE && isHosAddAllowed) ||
                                             (application == ApplicationEnum.AxxessHospice && isHosAddAllowed) ||
                                             (application == ApplicationEnum.AxxessPalliative && isPalAddAllowed) ||
                                             (application == ApplicationEnum.HomeCare && isHomeCareAddAllowed) ||
                                             (application == ApplicationEnum.AgencyCore && isHomeHealthAddAllowed)
                                                 ? ` <a
                                            class="dropdown-item font-size-sm bulk-reassign"
                                            href="javascript:void(0);"
                                            id="add-task-${resource.UserId}-${resource.AgencyId}"
                                        >
                                            Add Task
                                        </a>`
                                                 : ""
                                         }
                                         ${
                                             (application == ApplicationEnum.AxxessHospiceFE &&
                                                 hasSchedulerViewPermissionHos) ||
                                             (application == ApplicationEnum.AxxessHospice &&
                                                 hasSchedulerViewPermissionHos) ||
                                             (application == ApplicationEnum.AxxessPalliative &&
                                                 hasSchedulerViewPermissionPal) ||
                                             (application == ApplicationEnum.HomeCare &&
                                                 hasSchedulerViewPermissionHomeCare) ||
                                             (application == ApplicationEnum.AgencyCore &&
                                                 hasSchedulerViewPermissionHomeHealth)
                                                 ? this.isPremiumByAgency?.[resource.AgencyId]
                                                     ? `<a class="dropdown-item font-size-sm bulk-reassign border-top near-ai-button" href="javascript:void(0);" id="find-near-by-patients-${resource.UserId}-${resource.AgencyId}">Find Nearby Patients</a>`
                                                     : `<div class="dropdown-item font-size-sm border-top near-ai-button ai-disabled" id="find-near-by-patients-${resource.UserId}-${resource.AgencyId}">Find Nearby Patients <img src="${require("../../../../../assets/images/lock.svg")}" />
                                            <span class="locked-premium-tooltip">Contact Axxess to unlock this <br /> premium Axxess intelligence feature.</span></div>`
                                                 : ""
                                         }

                                    </div>
                                </div>

                                <p class="mb-0 w-auto user-name text-truncate">
                                    ${userName}
                                    <span class="font-size-xs">${resource.Credentials}</span>
                                </p>
                                ${
                                    phoneNumber
                                        ? `<a href="tel:${phoneNumber}" class="mb-0 font-size-xs w-auto">${phoneNumber}</a>`
                                        : ""
                                }
                                <p class="w-100 font-size-2xs text-truncate mb-0">${resource.AgencyName}</p>
                                <p class="fw-bold status-label mb-0 font-size-2xs">Title:<span class="text-capitalize text-truncate fw-normal">
                                    ${resource.Title}
                                 </span></p>
                                <p class="fw-bold status-label mb-0 font-size-2xs ${this.getStatusText(
                                    resource.Status
                                ).toLowerCase()}">
                                    Status : <span class="text-capitalize"> ${this.getStatusText(
                                        resource.Status
                                    )} </span>
                                </p>
                                <p class="fw-bold status-label mb-0 font-size-2xs">PayType:<span class="text-capitalize fw-normal"> ${this.PayType.getKeyUsingValue(
                                    resource.PayType
                                )} </span></p>


                            </div>`;
            }
            return userInfo;
        },

        generateEvent(arg: any, extraEvents: number) {
            let event = arg?.event?.extendedProps;
            let eventContent = "";
            const date = new Date(event.EndDate);
            const day = date.getDay();
            const saturday = 6;
            const showDeleteBtn: boolean =
                event.Status == VisitStatusEnum.NotStarted || event.Status == VisitStatusEnum.NotYetDue;
            if (!event) {
                return eventContent;
            }
            let eventStatus = "";
            let eventClass = "";
            let statusName = "";

            if (
                (event.Application === ApplicationEnum.AxxessHospice ||
                    event.Application === ApplicationEnum.AxxessPalliative) &&
                event.State === ScheduleLegendState.danger
            ) {
                statusName = `Missed Visit (${event.Status})`;
            } else {
                statusName = `${event.Status}`;
            }
            if (event.VisitConfirmed) {
                eventStatus = `<i class="fa fa-check-circle" aria-hidden="true"></i> Confirmed`;
                eventClass = "confirmed";
            } else if (event.PatientCalled) {
                eventStatus = `<i class="fa fa-exclamation-circle" aria-hidden="true"></i> Reschedule`;
                eventClass = "reschedule"; // using 'CalledPatient' temporary until we get veriable for "Call To Reschedule" from Solution
            } else if (event.RescheduleVisit) {
                eventClass = "reschedule";
                eventStatus = `<i class="fa fa-exclamation-circle" aria-hidden="true"></i> Reschedule`;
            }
            let isEventstatus = false;
            if (event.VisitConfirmed || event.PatientCalled || event.RescheduleVisit) {
                isEventstatus = true;
            }

            let evvHouse = null;
            if (event.Application == ApplicationEnum.HomeCare) {
                if (
                    event.EvvIconStatus === EvvStatusHomeCareEnum.Finished ||
                    event.EvvIconStatus === EvvStatusHomeCareEnum.FinishedAdjusted
                ) {
                    evvHouse =
                        event.EvvIconStatus === EvvStatusHomeCareEnum.FinishedAdjusted
                            ? `<a class='evv'><i class='evv-adjusted signed'></i></a>`
                            : `<a class='evv'><i class='fa fa-home fa-md signed'></i></a>`;
                } else if (
                    event.EvvIconStatus === EvvStatusHomeCareEnum.InProgress ||
                    event.EvvIconStatus === EvvStatusHomeCareEnum.InProgressAdjusted
                ) {
                    evvHouse =
                        event.EvvIconStatus === EvvStatusHomeCareEnum.InProgressAdjusted
                            ? `<a class='evv'><i class='evv-adjusted in-progress'></i></a>`
                            : `<a class='evv'><i class='fa fa-home fa-md'></i></a>`;
                } else if (event.EvvIconStatus === EvvStatusHomeHealthEnum.NotStarted) {
                    evvHouse = `<a class='evv gray-house'><i class='fa fa-home fa-md not-started'></i></a>`;
                }
            } else if (event.Application == ApplicationEnum.AgencyCore) {
                if (event.VerificationVisitStatus === EvvStatusHomeHealthEnum.InProgress) {
                    evvHouse = `<a class='evv ps-1'><i class='fa fa-home fa-md'></i></a>`;
                } else if (
                    event.VerificationVisitStatus === EvvStatusHomeHealthEnum.PatientVerified ||
                    event.VerificationVisitStatus === EvvStatusHomeHealthEnum.ClinicianVerified
                ) {
                    evvHouse = `<a class='evv'><i class='fa fa-home fa-md signed'></i></a>`;
                } else if (event.VerificationVisitStatus === EvvStatusHomeHealthEnum.NotStarted) {
                    evvHouse = `<a class='evv gray-house'><i class='fa fa-home fa-md not-started'></i></a>`;
                }
            }

            let isNonPatientVisit = !event.PatientId;
            let patientName = "";
            if (!isNonPatientVisit && !!event) {
                patientName = `<span class="d-flex align-items-center font-size-2xs">
                <i class="fas fa-user me-1"></i>
                <span>
                    ${this.getUserName(event.PatientFirstName, event.PatientLastName)}
                </span>
            </span>`;
            }
            let blueNote = null as any;
            let yellowNote = null as any;
            let redNote = null as any;
            let orangeNote = null as any;
            event.Comments?.map((comment: any) => {
                if (comment.CommentColor === "Blue") {
                    blueNote = {
                        id: event.PatientId,
                        element: `<i id=${`blue-tooltip-${event.Id}`} class="me-2 fas fa-sticky-note blue-note cursor-pointer"></i>`
                    };
                }
                if (comment.CommentColor === "Yellow") {
                    yellowNote = {
                        id: event.PatientId,
                        element: `<i id=${`yellow-tooltip-${event.Id}`} class="me-2 fas fa-sticky-note yellow-note cursor-pointer"></i>`
                    };
                }
                if (comment.CommentColor === "Red") {
                    redNote = {
                        id: event.PatientId,
                        element: `<i id=${`red-tooltip-${event.Id}`} class="me-2 fas fa-sticky-note red-note  cursor-pointer"></i>`
                    };
                }
                if (comment.CommentColor === "Orange") {
                    orangeNote = {
                        id: event.PatientId,
                        element: `<i id=${`orange-tooltip-${event.Id}`} class="me-2 fas fa-sticky-note orange-note  cursor-pointer"></i>`
                    };
                }
            });
            let showRestoreButton = false;
            if (event.Application === ApplicationEnum.AgencyCore || event.Application === ApplicationEnum.HomeCare) {
                showRestoreButton = event.State === 3;
            } else if (
                (event.Application === ApplicationEnum.AxxessPalliative ||
                    event.Application === ApplicationEnum.AxxessHospice ||
                    event.Application === ApplicationEnum.AxxessHospiceFE) &&
                event.ShowRestoreMissedVisit
            ) {
                const isLoggedInUsersTask =
                    this.agencyList?.findIndex(
                        (agency) => agency.application === event.Application && agency.userId === event.UserId
                    ) !== -1;
                showRestoreButton = isLoggedInUsersTask;
            }
            eventContent = `<div class="ax-calendar-event ${ScheduleLegendState[event.State]}" id = "scheduled-event-${
                event.Id
            }">
                <span class="w-100 d-flex justify-content-between">
                    <span class="fw-bold text-truncate font-size-xs">
                            ${event.TaskName}
                    </span>
                    <span class="btn-group table-btn-group ms-auto" id = "edit-icon-${event.Id}">
                        <button type="button" class="btn btn-xs px-1 py-0 btn-outline-primary dropdown-toggle" data-bs-toggle="dropdown" id = "edit-btn-${
                            event.Id
                        }">
                            <i class="fas fa-ellipsis-h"></i>
                        </button>
                        <div class="dropdown-menu ${day === saturday ? "" : "dropdown-transform-right mt-4"}">
                           ${
                               event.IsNonVisitAssignment
                                   ? ""
                                   : `<a
                                class="dropdown-item"
                                href="javascript:void(0);"
                                id = "btn-reassign-${event.Id}"
                            >
                            Reassign
                            </a>`
                           }
                            <a
                                class="dropdown-item"
                                href="javascript:void(0);"
                                id = "btn-visitdetails-${event.Id}"
                            >
                            Details
                            </a>
                            ${
                                event.IsNonVisitAssignment
                                    ? ""
                                    : `<a
                                class="dropdown-item"
                                href="javascript:void(0);"
                                id = "btn-profile-${event.Id}"
                            >
                            Profile
                            </a>`
                            }
                            ${
                                event.State !== ScheduleLegendState["non-visit-bg"]
                                    ? `<a
                                class="dropdown-item"
                                href="javascript:void(0);"
                                id = "btn-patientchart-${event.Id}"
                            >
                            Patient Chart
                            </a>`
                                    : ""
                            }
                            ${
                                showDeleteBtn || event.IsNonVisitAssignment
                                    ? `<a
                                  class="dropdown-item"
                                  href="javascript:void(0);"
                                  id="btn-deletevisit-${event.Id}"
                                >
                                Delete
                                </a>`
                                    : ""
                            }
                             ${
                                 showRestoreButton && !event.IsNonVisitAssignment
                                     ? `<a
                                  class="dropdown-item"
                                  href="javascript:void(0);"
                                  id="btn-restorevisit-${event.Id}"
                                >
                                Restore
                                </a>`
                                     : ""
                             }
                        </div>
                    </span>
                </span>
                <span class="w-100 font-size-3xs text-truncate ${event.StartTime ? `padding-top` : ``}">
                ${
                    event.Application === ApplicationEnum.HomeCare || event.Application === ApplicationEnum.AgencyCore
                        ? formatScheduledTime(event.StartTime) !== formatScheduledTime(event.EndTime)
                            ? formatTimeInOut(formatScheduledTime(event.StartTime), formatScheduledTime(event.EndTime))
                            : ""
                        : formatTimeInOut(formatScheduledTime(event.StartTime), formatScheduledTime(event.EndTime))
                }

                </span>
                <span class="w-100 font-size-3xs text-truncate padding-top">
                    ${statusName}
                </span>
                <span class="d-flex align-items-center w-100 justify-content-between font-size-3xs fw-semibold padding-top">
                    ${
                        event.IsNonVisitAssignment
                            ? ""
                            : `<span>
                        <i class="fas fa-user"></i>
                        ${this.getUserName(event.PatientFirstName, event.PatientLastName)}
                    </span>
                    <span id='evv-house-${event.Id}'>
                        ${evvHouse ? evvHouse : ""}
                    </span>`
                    }
                </span>
                <span class="d-flex">
                 <span id='blue-note-${event.Id}'>
                        ${blueNote ? blueNote.element : ""}
                    </span>
                    ${
                        ((event.Application === ApplicationEnum.AxxessHospice ||
                            event.Application === ApplicationEnum.AxxessHospiceFE) &&
                            !this.canViewTaskNotesHos) ||
                        (event.Application === ApplicationEnum.AxxessPalliative && !this.canViewTaskNotesPal)
                            ? ""
                            : `<span id='yellow-note-${event.Id}'>
                        ${yellowNote ? yellowNote.element : ""}
                    </span>`
                    }

                    <span id='red-note-${event.Id}'>
                        ${redNote ? redNote.element : ""}
                    </span>
                    <span id='orange-note-${event.Id}'>
                        ${orangeNote ? orangeNote.element : ""}
                    </span>
                </span>
                <span class="d-flex w-100 justify-content-between align-items-center ${
                    isEventstatus || event?.OnCall || event.PRN ? `padding-top` : ``
                }">
                    ${
                        isEventstatus
                            ? `<span class="status-${eventClass} fw-semibold">
                                ${eventStatus}
                            </span>`
                            : ``
                    }
                    ${event?.OnCall ? `<span class="pill pill-oncall"> On-Call </span>` : ``}
                    ${event.PRN ? `<span class="pill pill-prn"> PRN </span>` : ``}
                </span>
            </div>`;
            if (!!event.isExtraEvent) {
                eventContent = `<a class="cursor-pointer text-dark more-btn" tabindex="0">+ ${extraEvents} more</a>`;
            }
            return eventContent;
        },

        registerClick(arg: any) {
            let element = arg?.el?.className.split(" ").join(".");
            let extendedProps = arg?.resource?.extendedProps;
            $(`.${element}`).on("click", `#${extendedProps?.UserId}-${extendedProps.Application}`, (event: any) => {
                this.handleUserNameClick(extendedProps);
            });
            $(`.${element}`).on(
                "click",
                `#task-manager-${extendedProps?.UserId}-${extendedProps.AgencyId}`,
                (event: any) => {
                    this.openTaskManager(extendedProps);
                }
            );
            $(`.${element}`).on(
                "click",
                `#add-task-${extendedProps?.UserId}-${extendedProps.AgencyId}`,
                (event: any) => {
                    this.scheduleNewTasks(extendedProps);
                }
            );
            $(`.${element}`).on(
                "click",
                `#find-near-by-patients-${extendedProps?.UserId}-${extendedProps.AgencyId}`,
                (event: any) => {
                    if ($(event.currentTarget).hasClass("ai-disabled")) {
                        return;
                    }
                    this.toggleFindNearByClientsView(extendedProps);
                }
            );
        },

        async scheduleNewTasks(employee: BulkReassign) {
            // will be implemented later
            const employeeDetails = this.usersList.find((user: CalendarEmployeeModel) => employee.Id == user.Id);
            const employeeInfo = (({ FirstName, LastName }) => ({ FirstName, LastName }))(employeeDetails);
            const visitDetails: BulkReassign = Object.assign({}, employeeInfo, employee);
            if (!!visitDetails) {
                this.dialogRef = this.$dialog.open(ScheduleVisitEmployee, {
                    props: {
                        modal: true,
                        closeOnEscape: false,
                        showHeader: false
                    },
                    data: {
                        visitDetails
                    },
                    onClose: async (options: any) => {
                        if (options.data === "success") {
                            this._analyticsService.logEvent({
                                category: AnalyticsCategory.CentralVisitActions,
                                action: "Central Employee calendar Add task"
                            });
                            await this.onCalendarRefresh();
                        }
                        if (options.data === "reopen") {
                            await this.scheduleNewTasks(employee);
                        }
                    }
                });
            }
        },

        async deleteTask(args: any) {
            const events = args?.event?.extendedProps as IVisitDetails;
            try {
                let promptOptions: IPromptOptions = {
                    message: "Are you sure you want to delete selected task?",
                    okText: "Yes",
                    cancelText: "No"
                };
                const eventId = [events.Id] as string[];

                this.dialogRef = this.$dialog.open(Prompt, {
                    props: {
                        modal: true,
                        contentStyle: {
                            overflowX: "hidden"
                        },
                        showHeader: false
                    },

                    data: {
                        promptOptions
                    },

                    onClose: async (options: any) => {
                        if (options.data == "success") {
                            this.showLoading(true);
                            try {
                                if (events.Application == ApplicationEnum.AgencyCore) {
                                    const deleteTaskPayload: IDeleteTaskPayload = {
                                        AgencyId: events.AgencyId,
                                        Application: events.Application,
                                        PatientId: events.PatientId,
                                        EpisodeId: events.EpisodeId,
                                        EventId: events.Id,
                                        UserId: events.UserId
                                    };
                                    const res = await this._taskService.deleteTask(deleteTaskPayload);
                                    if (!res?.isSuccessful) {
                                        this.showLoading(false);
                                        this._toastrService.error({
                                            title: `Error - Deleting task`,
                                            message: res?.message
                                                ? res?.message
                                                : `There was an error deleting the visit. Please Try again later.`
                                        });
                                        return;
                                    }
                                } else {
                                    const deleteTaskPayload: IBulkDeletePayload = {
                                        AgencyId: events.AgencyId,
                                        Application:
                                            events.Application == ApplicationEnum.AxxessHospice
                                                ? ApplicationEnum.AxxessHospiceFE
                                                : events.Application,
                                        PatientId: events.PatientId,
                                        EventId: eventId,
                                        IsPatient: true
                                    };
                                    const res = await this._taskService.patientBulkDelete(deleteTaskPayload);
                                    if (!res?.isSuccessful) {
                                        throw Error;
                                    }
                                }
                                this.$emit("calendarRefresh");
                                this.showLoading(false);
                                this._toastrService.success({
                                    message: `The task has been successfully deleted.`,
                                    title: `Task Deleted`
                                });
                            } catch (error) {
                                this.showLoading(false);
                                this._toastrService.error({
                                    title: `Error - Deleting task`,
                                    message: `There was an error deleting the visit. Please Try again later.`
                                });
                            }
                        }
                    }
                });
            } catch (error) {
                this._toastrService.error({
                    title: `Error - Task`,
                    message: `There was a problem while deleting the tasks. Please try again.`
                });
            }
        },

        async initiateRestoreTask(args: any) {
            const events = args?.event?.extendedProps as IVisitDetails;
            try {
                const promptOptions: IPromptOptions = {
                    message: "Are you sure you want to restore selected task?",
                    okText: "Yes",
                    cancelText: "No"
                };
                const eventId = [events.Id] as string[];

                this.dialogRef = this.$dialog.open(Prompt, {
                    props: {
                        modal: true,
                        contentStyle: {
                            overflowX: "hidden"
                        },
                        showHeader: false
                    },

                    data: {
                        promptOptions
                    },

                    onClose: async (options: any) => {
                        if (options.data == "success") {
                            await this.restoreMissedVisit(events);
                        }
                    }
                });
            } catch (error) {
                this._toastrService.error({
                    title: `Error - Task`,
                    message: `There was a problem while restoring the tasks. Please try again.`
                });
            }
        },

        async restoreMissedVisit(events: IVisitDetails) {
            try {
                const payload: IRestoreMissedVisitRequest = {
                    AgencyId: events.AgencyId,
                    EventId: events.Id,
                    // This is done because Application property of AxxessHospiceFE tasks were updated to AxxessHospice in taskActions()
                    // so we are changong it back
                    Application:
                        events.Application === ApplicationEnum.AxxessHospice
                            ? ApplicationEnum.AxxessHospiceFE
                            : events.Application,
                    UserId: events.UserId
                };
                if (events.Application == ApplicationEnum.AgencyCore) {
                    payload.EpisodeId = events.EpisodeId;
                    payload.PatientId = events.PatientId;
                } else if (events.Application == ApplicationEnum.HomeCare) {
                    payload.PatientId = events.PatientId;
                }
                this.showLoading(true);
                const firstResponse = await this._taskService.restoreMissedVisit(payload);
                if (!firstResponse.IsSuccessful && firstResponse.ErrorDetails) {
                    if (!firstResponse.ErrorDetails.TaskIssues.length) {
                        throw new Error(firstResponse.ErrorMessage);
                    }

                    const validationList: IRescheduleValidations[] = firstResponse.ErrorDetails.TaskIssues.map(
                        (item: any) => {
                            return {
                                title: item.Title as string,
                                description: [item.Message] as string[],
                                taskIssueSurpressKey: item.TaskIssueSurpressKey as string
                            };
                        }
                    );

                    const hasError = firstResponse.ErrorDetails.AllTasksHaveErrors;

                    this.showLoading(false);
                    this.dialogRef = this.$dialog.open(RescheduleValidation, {
                        props: {
                            modal: true,
                            closeOnEscape: false,
                            showHeader: false,
                            style: {
                                width: "60vw"
                            }
                        },

                        data: {
                            validations: validationList,
                            name: "Employee",
                            component: "Restore-visit",
                            hasError
                        },

                        onClose: async (options: any) => {
                            if (options.data == "cancel") {
                                return;
                            } else {
                                const issuesResponseObj: IHandleTaskReschuleIssue = {};
                                for (let issue of validationList) {
                                    issuesResponseObj[issue.taskIssueSurpressKey] = "ScheduleAllAnyways";
                                }

                                this.showLoading(true);
                                const secondResponse = await this._taskService.restoreMissedVisit({
                                    ...payload,
                                    handleTaskIssues: issuesResponseObj
                                });
                                if (!secondResponse.IsSuccessful) {
                                    throw new Error(secondResponse.ErrorMessage);
                                } else {
                                    this.$emit("calendarRefresh");
                                    this.showLoading(false);
                                    this._toastrService.success({
                                        message: `The task has been successfully restored.`,
                                        title: `Task Restored`
                                    });
                                }
                            }
                        }
                    });
                } else if (!firstResponse.IsSuccessful) {
                    throw new Error(firstResponse.ErrorMessage);
                } else {
                    this.showLoading(false);
                    this.$emit("calendarRefresh");
                    this._toastrService.success({
                        message: `The task has been successfully restored.`,
                        title: `Task Restored`
                    });
                }
            } catch (error) {
                this.showLoading(false);
                this._toastrService.error({
                    title: `Error - Restoring task`,
                    message: error ? error : `There was an error restoring the visit. Please Try again later.`
                });
            }
        },

        async openTaskManager(employee: BulkReassign) {
            this._analyticsService.logEvent({
                category: AnalyticsCategory.CentralVisitActions,
                action: "Central Employee Task manager"
            });
            // will be implemented later
            // This has to be done because backend is sending -3 for hospice solution.
            let application;
            if (employee.Application === ApplicationEnum.AxxessHospice) {
                application = ApplicationEnum.AxxessHospiceFE;
            } else {
                application = employee.Application;
            }
            this.$router.push({
                path: "/axxess-central/task-manager?",
                query: {
                    agencyId: employee.AgencyId,
                    fullname: `${employee.FirstName} ${employee.LastName} ${employee.Credentials}`,
                    application: application,
                    userId: employee.UserId
                }
            });
        },

        async getDisciplineTasks(visitDetails: IVisitDetails) {
            try {
                const payload: IGetDisciplineTaskHHParams = {
                    agencyId: visitDetails.AgencyId,
                    application: visitDetails.Application
                };
                this.disciplineTasks = await this._lookupService.getDisciplineTasks(payload);
            } catch (e) {
                this._toastrService.error({
                    title: `Fetching Discipline Task Failed`,
                    message: `Fetching Discipline Task Operation Failed, Please Contact Axxess if issue still persists.`
                });
            }
        },

        async handleUserNameClick(resource: any) {
            let redirectResponse;
            let action = "";
            if (resource.Application === ApplicationEnum.AxxessHospice) {
                redirectResponse = {
                    redirectUrl: `${getHospiceRedirectionUrl(resource.AgencyId)}/employees/employee-center/${
                        resource.UserId
                    }?accountId=${resource.AgencyId}`,
                    isSuccessful: true
                };
                action = "AxxessHospice";
            } else if (resource.Application === ApplicationEnum.AxxessPalliative) {
                redirectResponse = {
                    redirectUrl: `${config.palliativeUrl}/employees/employee-center/${resource.UserId}?accountId=${resource.AgencyId}`,
                    isSuccessful: true
                };
                action = "AxxessPalliative";
            } else {
                const isClinician = this._permissionManager.checkPermission(
                    ParentPermissionEnum.clinician,
                    PermissionActionEnum.canView
                );
                this.showLoading(true);
                redirectResponse = await this._navigationService.navigateToUser({
                    agencyId: resource.AgencyId,
                    applicationId: resource.Application,
                    isClinician: isClinician,
                    userId: resource.UserId
                });
                action = resource.Application === ApplicationEnum.AgencyCore ? "AxxessHomeHealth" : "AxxessHomeCare";
                this.showLoading(false);
            }
            this._navigationService.redirect(redirectResponse, {
                category: AnalyticsCategory.CentralCalendarNavigation,
                action
            });
        },

        // NOTE: Uncomment when task navigation is needed
        // async handleEventClick(eventObjectInput: any | ITaskByPatientResponse) {
        //     let eventData = eventObjectInput?.event?.extendedProps;
        //     await this.navigateToTask(eventData);
        // },

        // async navigateToTask(eventData: ITaskByPatientResponse) {
        //     let redirectResponse;
        //     let analyticsCategory = "Calendar-Task-Navigation";
        //     let action = "";

        //     if (eventData.Application === 256) {
        //         redirectResponse = {
        //             redirectUrl: `${config.hospiceUrl}/note/${eventData.Id}?accountId=${eventData.AgencyId}`,
        //             isSuccessful: true
        //         };
        //         action = "AxxessHospice";
        //     } else {
        //         let accessRoles = await this._accessService.getAccessRoles();
        //         redirectResponse = await this._navigationService.navigateToTask({
        //             agencyId: eventData.AgencyId,
        //             applicationId: eventData.Application,
        //             userId: eventData.UserId,
        //             isClinician: accessRoles.isClinician,
        //             patientId: eventData.PatientId,
        //             taskId: eventData.Id,
        //             episodeId: eventData.EpisodeId
        //         });
        //         action = eventData.Application === ApplicationEnum.AgencyCore ? "AxxessHomeHealth" : "AxxessHomeCare";
        //     }

        //     this._navigationService.redirect(redirectResponse, {
        //         category: analyticsCategory,
        //         action
        //     });
        // },

        attachTooltip(arg: any) {
            let element = $(arg?.el);
            let content = this.getTooltipContent(arg?.event?.extendedProps);
            (element as any).popover({
                html: true,
                content: content,
                trigger: "hover",
                placement: "top",
                container: "body"
            });
        },

        getTooltipContent(data: any) {
            let content = "";
            let evvContent = "";
            if (this.getEvvContent(data).length > 0 && this.getEvvContent(data) != "Not Yet Started") {
                evvContent = `<span>
                                <strong>EVV Time:</strong>
                                ${this.getEvvContent(data)}
                            </span>`;
            }
            if (!!data) {
                content = `<div class="d-flex flex-column align-items-start event-tooltip font-size-xs">
                                <span><strong>Branch:</strong> ${data.BranchName}</span>
                                ${data.Payer ? `<span><strong>Payer:</strong> ${data.Payer}</span>` : ``}
                                <span><strong>Billable:</strong> ${!!data.IsBillable ? "Yes" : "No"}</span>
                                <span><strong>Payable:</strong> ${!!data.IsPayable ? "Yes" : "No"}</span>
                                ${
                                    data.ScheduledByFirstName
                                        ? `<span>
                                        <strong>Scheduled By:</strong>
                                            ${this.getUserName(data.ScheduledByFirstName, data.ScheduledByLastName)}
                                        </span>`
                                        : ``
                                }
                                ${evvContent}
                            <div>`;
            }
            return content;
        },

        attachNonVisitTooltip(arg: any) {
            let element = $(arg?.el);
            let content = this.getNonVisitTooltipContent(arg?.event?.extendedProps);
            (element as any).popover({
                html: true,
                content: content,
                trigger: "hover",
                placement: "auto",
                container: "body"
            });
        },

        getNonVisitTooltipContent(data: any) {
            let content = "";
            if (!!data) {
                content = `<div class="d-flex flex-column align-items-start event-tooltip font-size-xs">
                                <span><strong>Mileage:</strong> ${data.Mileage || "Not Available"}</span>
                            <div>`;
            }
            return content;
        },

        formatEVVTime(startTime: Date | string, endTime: Date | string) {
            if (startTime || endTime) {
                if (startTime === "Not Collected" || endTime === "Not Collected") {
                    return `${
                        startTime === "Not Collected" ? "Not Collected" : moment(startTime).format("hh:mm A")
                    } - ${endTime === "Not Collected" ? "Not Collected" : moment(endTime).format("hh:mm A")}`;
                }
                if (moment(startTime).isBefore(endTime, "day")) {
                    return `${moment(startTime).format("hh:mm A")} - ${moment(endTime).format("MM/DD/YYYY hh:mm A")}`;
                } else {
                    return `${moment(startTime).format("hh:mm A")} - ${moment(endTime).format("hh:mm A")}`;
                }
            } else return "";
        },

        getEvvContent(data: any) {
            let evvTime = "";
            if (data.Application === ApplicationEnum.AxxessHospiceFE) {
                return evvTime;
            } else if (!data.EvvTimeIn && !data.EvvTimeOut) {
                evvTime = "Not Yet Started";
            } else {
                const evvTimeIn = data.EvvTimeIn || "Not Collected";
                const evvTimeOut = data.EvvTimeOut || "Not Collected";
                evvTime = this.formatEVVTime(evvTimeIn, evvTimeOut);
            }
            return evvTime;
        },

        getUserName(firstName: string, lastName: string) {
            let userName = "";
            if (!firstName && !lastName) {
                userName = "Unassigned";
            } else {
                userName = formatName(firstName, lastName, "FN LN");
            }
            return userName;
        },

        async rescheduleVisitHomeCare(
            event: any,
            StartDate: string,
            EndDate: string,
            UserId: string,
            isReassign: boolean
        ) {
            const { Id, AgencyId, Application, EpisodeId, PatientId, TimeIn, TimeOut } = event as IVisitDetails;
            const rescheduleDetails: IReassignCalendarVisitHC = {
                AgencyId,
                PatientId,
                EventId: Id,
                EpisodeId,
                Application,
                UserId,
                TimeIn,
                TimeOut,
                VisitStartTime: StartDate,
                VisitEndTime: EndDate,
                isDragDrop: true,
                Comments: this.getComment(event)
            };
            if (this.trackingReasonId && this.trackingTypeId) {
                rescheduleDetails.TypeId = this.trackingTypeId;
                rescheduleDetails.ReasonId = this.trackingReasonId;
                this.trackingTypeId = "";
                this.trackingReasonId = "";
            }

            try {
                const firstResponse = await this._taskService.rescheduleTaskHC(rescheduleDetails);

                if (!firstResponse.isSuccessful && firstResponse.errorDetails) {
                    if (!firstResponse.errorDetails.taskIssues.length) {
                        throw new Error(firstResponse.errorMessage);
                    }

                    let validationList: IRescheduleValidations[] = firstResponse.errorDetails.taskIssues.map(
                        (item: any) => {
                            return {
                                title: item.title as string,
                                description: [item.message] as string[],
                                taskIssueSurpressKey: item.taskIssueSurpressKey as string
                            };
                        }
                    );

                    const hasError = firstResponse.errorDetails.allTasksHaveErrors;

                    this.dialogRef = this.$dialog.open(RescheduleValidation, {
                        props: {
                            modal: true,
                            closeOnEscape: false,
                            showHeader: false,
                            style: {
                                width: "60vw"
                            }
                        },

                        data: {
                            validations: validationList,
                            name: "Employee",
                            component: "Schedule-visit",
                            hasError
                        },

                        onClose: async (options) => {
                            if (options.data == "cancel") {
                                return;
                            } else {
                                const issuesResponseObj: IHandleTaskReschuleIssue = {};
                                for (let issue of validationList) {
                                    issuesResponseObj[issue.taskIssueSurpressKey] = "ScheduleAllAnyways";
                                }

                                rescheduleDetails.HandleTaskIssues = issuesResponseObj;
                                const secondResponse = await this._taskService.rescheduleTaskHC(rescheduleDetails);
                                if (!secondResponse.isSuccessful) {
                                    throw new Error(secondResponse.errorMessage);
                                }

                                if (secondResponse.isSuccessful) {
                                    this._toastrService.success({
                                        title: `Visit ${isReassign ? "Reassigned" : "Rescheduled"}`,
                                        message: `Visit has been successfully ${isReassign ? "reassigned" : "rescheduled"}.`
                                    });
                                    this.$emit("calendarRefresh");
                                }
                            }
                        }
                    });
                }

                if (firstResponse.isSuccessful && !firstResponse.errorDetails) {
                    this._toastrService.success({
                        title: `Visit ${isReassign ? "Reassigned" : "Rescheduled"}`,
                        message: `Visit has been successfully ${isReassign ? "reassigned" : "rescheduled"}.`
                    });
                    this.$emit("calendarRefresh");
                }
            } catch (error) {
                this._toastrService.error({
                    title: `Error - ${isReassign ? "Reassigning" : "Rescheduling"} task`,
                    message:
                        error.message ||
                        `There was an error ${isReassign ? "reassigning" : "rescheduling"} the visit. Please Try again later.`
                });
            } finally {
                await this.onCalendarRefresh();
            }
        },

        async rescheduleVisitHosPal(event: IVisitDetails, StartDate: string, EndDate: string, UserId: string) {
            let application;
            if (event.Application === ApplicationEnum.AxxessHospice) {
                application = ApplicationEnum.AxxessHospiceFE;
            } else {
                application = event.Application;
            }
            const { Id, AgencyId } = event;
            const rescheduleDetails: IRescheduleVisitHosPal = {
                TaskId: Id,
                AccountId: AgencyId,
                StartDate,
                EndDate,
                UserId,
                Application: application
            };
            try {
                await this._taskService.rescheduleTaskHosPal(rescheduleDetails);
                this._toastrService.success({
                    title: `Success`,
                    message: `The visit has been successfully rescheduled.`
                });
            } catch (error) {
                this._toastrService.error({
                    title: `Error - Rescheduling task`,
                    message: `There was an error rescheduling the visit. Please Try again later.`
                });
            } finally {
                await this.onCalendarRefresh();
            }
        },

        attachMorePopover(arg: any) {
            let element = $(arg?.el).find(".more-btn");
            let extendedProps = arg?.event?.extendedProps;
            let startDate = new Date(Date.parse(extendedProps?.StartDate));
            let key = `${extendedProps.UserId}${startDate.toLocaleDateString()}`;
            let tasksList = this.tasksMap?.get(key)?.slice(this.eventLimit, this.tasksList?.length);
            let content = "";
            tasksList?.map((task: any) => {
                let isNonPatientVisit = !task.PatientId;
                let patientName = "";
                if (!isNonPatientVisit) {
                    patientName = `<span class="d-flex align-items-center font-size-2xs">
                    <i class="fas fa-user me-1"></i>
                    <span>
                        ${this.getUserName(task.PatientFirstName, task.PatientLastName)}
                    </span>
                </span>`;
                }
                let taskContent = `<div class="ax-calendar-event ${
                    ScheduleLegendState[task.State]
                } mb-1 more-event" id="${task.Id}">
                    <span class="w-100 fw-bold text-truncate">
                        ${task.TaskName}
                    </span>
                    ${patientName}
                    <span class="w-100 font-size-3xs text-truncate">
                        ${task.Status}
                    </span>
                </div>`;
                content = content + taskContent;
                this.attachMoreEventsTooltip(task);
            });
            (element as any)
                .popover({
                    html: true,
                    content: content,
                    trigger: "click focus",
                    placement: "auto",
                    container: "body"
                })
                .on("mouseenter", () => {
                    let parentDiv = element.closest("td > div");
                    parentDiv.css("z-index", "auto");
                })
                .on("mouseleave", () => {
                    let parentDiv = element.closest("td > div");
                    parentDiv.css("z-index", "");
                });

            // $(".fc-timeline-event")
            //     .off("click")
            //     // tslint:disable-next-line: only-arrow-functions
            //     .on("click", ".more-event", async function (event: any) {
            //         event.stopPropagation();
            //         let taskId = $(this).prop("id");
            //         if (!!taskId) {
            //             let task = self.tasksList.find((item) => item.Id === taskId);
            //             if (!!task) {
            //                 await self.navigateToTask(task);
            //             }
            //         }
            //     });
        },

        attachMoreEventsTooltip(task: ITaskByUserResponse) {
            let isNonPatientVisit = !task.PatientId;
            let content = isNonPatientVisit ? this.getNonVisitTooltipContent(task) : this.getTooltipContent(task);
            $("body").on("mouseenter", `#${task.Id}`, () => {
                $(`#${task.Id}`)
                    .popover({
                        html: true,
                        content: content,
                        trigger: "hover",
                        placement: "top",
                        container: "body"
                    })
                    .popover("show");
            });
        },

        openVisitNote(arg: any, color: string) {
            let noteTitle = "";
            const application = arg?.event?.extendedProps.Application;
            switch (color) {
                case CommentColorEnum.yellow:
                    if (application === ApplicationEnum.HomeCare) {
                        noteTitle = "Visit Comments";
                    } else {
                        noteTitle = "Task Notes";
                    }
                    break;
                case CommentColorEnum.blue:
                    if (application === ApplicationEnum.HomeCare) {
                        noteTitle = "Care Period Comments";
                    } else {
                        noteTitle = "Episode Comments";
                    }
                    break;
                case CommentColorEnum.red:
                    if (application === ApplicationEnum.AgencyCore) {
                        noteTitle = "Missed/Returned Note";
                    } else if (application === ApplicationEnum.HomeCare) {
                        noteTitle = "Missed Comments";
                    } else {
                        noteTitle = "Missed Note";
                    }
                    break;
                case CommentColorEnum.orange:
                    if (application === ApplicationEnum.HomeCare) {
                        noteTitle = "Returned Comments";
                    } else {
                        noteTitle = "Returned Note";
                    }
                    break;
            }
            const scheduledEvent = arg?.event?.extendedProps;
            let element = $(arg?.el).find(`#${color}-note-${scheduledEvent.Id}`);
            if (!!$(arg?.el).find(`#${color}-note-${scheduledEvent.Id}`).length) {
                let content = this.getNotePopContent(scheduledEvent, color);
                (element as any).popover({
                    html: true,
                    content: content,
                    title: `<div class="d-flex justify-content-between">
                    <span>${noteTitle}</span>
                    <i class="fas fa-times cursor-pointer local-popup-close" id="care-period-${scheduledEvent.Id}"></i>
                    </div>`,
                    trigger: "click",
                    placement: "right",
                    container: "body",
                    customClass: `visit-${color}-popup-note`,
                    sanitize: false
                });

                (element as any).on("shown.bs.popover", () => {
                    const closeBtn = document.getElementById(`care-period-${scheduledEvent.Id}`);
                    closeBtn?.addEventListener("click", () => (element as any).popover("hide"), { once: true });
                });
            }
        },
        getNotePopContent(content: any, color: string) {
            if (color === CommentColorEnum.red || color === CommentColorEnum.orange) {
                const details = content.Comments.find(
                    (comment: any) => comment.CommentColor === color.charAt(0).toUpperCase() + color.slice(1)
                );
                const text =
                    details.CommentType === StickyNoteTypeEnum.MissedVisit
                        ? `<span style="display:block";>${details.Comment}</span>`
                        : details.MultiComments.map(
                              (comment: string) => `<span style="display:block";>${comment}</span>`
                          ).join("");
                return `<div class="visit-${color}-popup-note">${text}</div>`;
            } else {
                const textList = content.Comments.find(
                    (comment: any) => comment.CommentColor === color.charAt(0).toUpperCase() + color.slice(1)
                );
                const text = textList?.MultiComments?.map((comment: string) => {
                    const lines = comment.split("\n");
                    let displayLine = lines
                        ?.map((line, index) => {
                            if (lines.length === index + 1) {
                                if (`**${textList?.Comment}**` === line) {
                                    return `<span style="display:block";>${line.slice(2, line.length - 2)}</span>`;
                                }
                            }
                            if (index === 0) {
                                return `<span style="display:block; color:red">${line.trim()}</span>`;
                            } else if (index === 2) {
                                return `<span style="display:block; color:gray; font-size:10px">${line.trim()}</span>`;
                            }
                            return `<span style="display:block;">${line.trim()}</span>`;
                        })
                        .join("");
                    return `<div style="margin-bottom:12px"> ${displayLine}</div>`;
                }).join("");
                return `<div class="visit-${color}-popup-note">${text}</div>`;
            }
        },

        attachVisitTooltip(arg: any, color: string) {
            const scheduledEvent = arg?.event?.extendedProps;
            if (!!$(arg?.el).find(`#${color}-tooltip-${scheduledEvent.Id}`).length) {
                let element = $(arg?.el).find(`#${color}-tooltip-${scheduledEvent.Id}`);
                let content = this.getNoteTooltipContent(arg?.event?.extendedProps, color);
                (element as any).tooltip({
                    html: true,
                    title: content,
                    container: "body",
                    customClass: `visit-${color}-note`
                });

                (element as any).tooltip("show");
            }
        },

        getNoteTooltipContent(data: any, color: string) {
            if (color === CommentColorEnum.red || color === CommentColorEnum.orange) {
                const details = data.Comments.find(
                    (comment: any) => comment.CommentColor === color.charAt(0).toUpperCase() + color.slice(1)
                );
                let allComments =
                    details.CommentType === StickyNoteTypeEnum.MissedVisit
                        ? details.Comment
                        : this.getTaskReturnComments(details.MultiComments);
                const text = `<div class="visit-${color.toLowerCase()}-note">
                        <span style="display:block";>${allComments}</span>
                </div>`;
                return text;
            } else {
                const textList = data.Comments.find(
                    (comment: any) => comment.CommentColor === color.charAt(0).toUpperCase() + color.slice(1)
                );
                let text = textList && textList.MultiComments ? textList.MultiComments[0] : "";
                text = `**${textList?.Comment}**` === text ? text.slice(2, text.length - 2) : text;
                if (color === CommentColorEnum.yellow) {
                    text = this.formatAndTruncateMessage(text);
                }
                return `<div class="visit-${color.toLowerCase()}-note">${text}</div>`;
            }
        },

        formatAndTruncateMessage(text: string) {
            const [sender, message, timestamp] = text
                .split("\n")
                .map((line) => line.trim())
                .filter(Boolean);

            const truncatedMessage = truncateText(message, 50);

            return `${sender}<br>${truncatedMessage}<br>${timestamp}`;
        },

        getComment(content: IGetTaskResponseByUser): string {
            if (content.Comments.length === 0) return "";
            const response = content.Comments.find(
                (comment: IGetTaskComment) =>
                    comment.CommentColor.toLowerCase() === CommentColorEnum.yellow.toLowerCase()
            );
            return response?.Comment;
        },
        handleEventDrag(e: any) {
            const container = document.querySelector("#App__Calendar");
            const rect = container.getBoundingClientRect();
            if (e.clientY < rect.top + 50) {
                container.scrollTop -= 10; // scroll up
            } else if (e.clientY > rect.bottom - 50) {
                container.scrollTop += 10; // scroll down
            }
        },

        filterDuplicateEvent(event: any) {
            let calendarApi = event.view.calendar;
            let calendarloadedEvents = calendarApi.getEvents();
            if (calendarloadedEvents.length != this.taskActions.length) {
                let existingEvents = calendarloadedEvents;
                const uniqueEvents = Array.from(new Map(existingEvents.map((e) => [e.id, e])).values());
                calendarApi.removeAllEvents();
                uniqueEvents.forEach((e) => calendarApi.addEvent(e));
            }
        }
    }
});
